/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { isNonceEnabled, SDK_FRAGMENT_NAME } from '@msdyn365-commerce/core-internal';
import * as React from 'react';
import { IMfrmUtagDataViewProps } from './mfrm-utag-data';
import { IMfrmUtagDataData } from './mfrm-utag-data.data';
import { IMfrmUtagDataProps, pageType as PageTypeOptions } from './mfrm-utag-data.props.autogenerated';

export default (props: IMfrmUtagDataProps<IMfrmUtagDataData>) => {
    const source = getPageScript(props);
    const nonceToken = props.context && isNonceEnabled(props.context.request);
    const scriptHtml = `<script ${nonceToken ? `nonce='${nonceToken}'` : ''}>${source}</script>`;

    // tslint:disable-next-line: react-no-dangerous-html
    return React.createElement(SDK_FRAGMENT_NAME, { key: props.id, dangerouslySetInnerHTML: { __html: scriptHtml } });
};

const getPageScript = (props: IMfrmUtagDataProps<IMfrmUtagDataData>) => {
    // Build an array of utag data.
    const utag_extra = ['...utag_data', 'new_site: newSiteCookie'];

    // Append values to utag array.
    const pageTypeVars = getPageTypeVars(props);
    !!pageTypeVars && utag_extra.push(pageTypeVars);

    const pageContextTypeVars = getPageContextTypeVars(props);
    !!pageContextTypeVars && utag_extra.push(pageContextTypeVars);

    const productTypeVars = getProductTypeVars(props);
    !!productTypeVars && utag_extra.push(productTypeVars);

    const pageNameVars = getPageNameVars(props);
    !!pageNameVars && utag_extra.push(pageNameVars);

    // Merge values using a comma as glue.
    return `${getCookieFunction()}utag_data = { ${utag_extra.join(', ')} };`;
};

const getPageTypeVars = (props: IMfrmUtagDataViewProps) => {
    const { pageType } = props.config;

    if (pageType) {
        if (pageType === PageTypeOptions.search) {
            return `page_type: 'search', search_term: '${getSearchTerm(props)}'`;
        } else {
            return `page_type: '${pageType.toLowerCase()}'`;
        }
    } else {
        return '';
    }
};

const getPageNameVars = (props: IMfrmUtagDataProps<IMfrmUtagDataData>) => {
    const reqUrl = props.context.request.url.requestUrl.toString()?.split('?')[0];

    var pageNameToReturn = (a : string | undefined) => {
        return a?.replace(/'/g, "\\'");
    };

    if (reqUrl.endsWith('.c')) {
        return `page_name: '${pageNameToReturn(props.data.categoryPageSummary?.result?.title)}'`;
    } else if (reqUrl.endsWith('.p')) {
        return `page_name: '${pageNameToReturn(props.data.productPageSummary?.result?.title)}'`;
    } else {
        return `page_name: '${pageNameToReturn(props.context.request.telemetryPageName)}'`;
    }
};

const getPageContextTypeVars = (props: IMfrmUtagDataViewProps) => {
    const { pageContextType } = props.config;

    if (pageContextType) {
        return `page_context_type: '${pageContextType.toLowerCase()}'`;
    } else {
        return undefined;
    }
};

const getProductTypeVars = (props: IMfrmUtagDataProps<IMfrmUtagDataData>) => {
    const pageType = props.config.pageType?.toLocaleLowerCase();

    if (pageType === 'checkout' || pageType === 'cart' || pageType === 'product') {
        //product category
        const productData = props.data.listPageStateBuyBox?.result;
        const productName = productData?.name?.replace(/\'/g, `'`);
        const productCategory = productData?.categoryName?.replace(/\'/g, `'`);
        const productBrand = productData?.productBrandName?.replace(/\'/g, ``);
        if (productData && productData.itemId) {
            return `product_id: ['${productData.itemId}'],
            product_name: ['${productName}'],
            product_category: ['${productCategory}'],
            product_discount: ['0.00'],
            product_brand: ['${productBrand}'],
            product_variant: ['${productData.variantId}'],
            product_price: ['${productData.price}'],
            product_url:['${productData.productUrl}'],
            product_image_url: ['${productData.productImageUrl}'],
            product_size:['${productData.productSize}']`;
        } else {
            return undefined;
        }
    }
    return undefined;
};

const getSearchTerm = (props: IMfrmUtagDataViewProps) => {
    return props.context.request.query?.q;
};

const getCookieFunction = () => {
    // Build a cookie value getter in JS. We can't access the cookie data from this module.
    // Use the cookie values to set new_site and is_cart_active.
    return `
    let getCookieValue = (ck) => {
        let cookieValue,
            cookieKey = ck + '=',
            decodedCookie = decodeURIComponent(document.cookie),
            ca = decodedCookie.split(';');

        for (let i in ca)
        {
            // Trim left spaces
            let c = ca[i].replace(/^\\s+/, '');
            if (!!~c.indexOf(cookieKey)) {
                cookieValue = c.substring(cookieKey.length, c.length);
            }
        }

        return cookieValue;
    };

    var newSiteCookie = getCookieValue('d365_site') ? 'true' : 'false';
    var utag_data = typeof utag_data !== 'undefined' ? utag_data : {};
    const checkoutCheck = window.location.pathname.includes('checkout');
    const checkoutExpress = window.location.pathname.includes('paypal') || window.location.pathname.includes('applepay');
    var newCookies =  getCookieValue('mfrm_basket_cookie');
    let basketNewObj = newCookies && JSON.parse(newCookies);

    var newCookiesProductUrl =  getCookieValue('tealium_cart_products_url');
    var basketNewObjUrl = newCookiesProductUrl && JSON.parse(newCookiesProductUrl);

    var newCookiesProductData =  getCookieValue('tealium_cart_products_details');
    var newCookiesProductDataObj = newCookiesProductData && JSON.parse(newCookiesProductData);

    var newCookiesProductQuantity =  getCookieValue('tealium_cart_products_quantity');
    var newCookiesProductQuantityObj = newCookiesProductQuantity && JSON.parse(newCookiesProductQuantity);

    var newCookiesExpress =  getCookieValue('express_funnel');
    var usaEpayErrorCookie = getCookieValue('tealium_usaEpay_error');
    var usaEpayErrorCookieObj = usaEpayErrorCookie && JSON.parse(usaEpayErrorCookie);
    var isUsaEpay = getCookieValue('is_usa_epay');
    var cardType = getCookieValue('temp_card_payment_info_for_order_confirmation')?.split(',')[0];
    utag_data.cart_product_id = newCookiesProductDataObj && newCookiesProductDataObj.productIDsArray;
    utag_data.cart_product_category = newCookiesProductDataObj && newCookiesProductDataObj.productCatgoryArray;
    utag_data.cart_product_name = newCookiesProductDataObj && newCookiesProductDataObj.productNameArray;
    utag_data.cart_product_brand = newCookiesProductDataObj && newCookiesProductDataObj.productBrandName;
    utag_data.cart_product_quantity = newCookiesProductQuantityObj && newCookiesProductQuantityObj.productQuantity;
    utag_data.cart_product_variant = newCookiesProductDataObj && newCookiesProductDataObj.variantRecord;
    utag_data.cart_product_size = newCookiesProductDataObj && newCookiesProductDataObj.productSize;
    utag_data.cart_product_price = newCookiesProductDataObj && newCookiesProductDataObj.cartProductPriceArray;
    utag_data.cart_url = basketNewObjUrl && basketNewObjUrl.cartUrl;
    utag_data.cart_product_image_url = basketNewObjUrl && basketNewObjUrl.productImageUrl;
    utag_data.cart_product_url = basketNewObjUrl && basketNewObjUrl.productURL;
    utag_data.cart_total_quantity = newCookiesProductQuantityObj && newCookiesProductQuantityObj.quantity;
    utag_data.cart_subtotal = newCookiesProductQuantityObj && newCookiesProductQuantityObj.subTotal;
    utag_data.core_item_flag = newCookiesProductQuantityObj && newCookiesProductQuantityObj.isCoreItem;
    utag_data.funnel_type = newCookiesProductQuantityObj && newCookiesProductQuantityObj.funnelType;

    if(checkoutExpress) {
        utag_data.funnel_type = newCookiesExpress;
    }

    if(checkoutCheck || checkoutExpress) {
        utag_data.page_name = 'customer info';
        utag_data.product_discount = newCookiesProductDataObj && newCookiesProductDataObj.productDiscountArray;
        utag_data.product_brand = newCookiesProductDataObj && newCookiesProductDataObj.productBrandName;
        utag_data.product_id = newCookiesProductDataObj && newCookiesProductDataObj.productIDsArray;
        utag_data.product_category = newCookiesProductDataObj && newCookiesProductDataObj.productCatgoryArray;
        utag_data.product_name = newCookiesProductDataObj && newCookiesProductDataObj.productNameArray;
        utag_data.product_quantity = newCookiesProductQuantityObj && newCookiesProductQuantityObj.productQuantity;
        utag_data.product_variant = newCookiesProductDataObj && newCookiesProductDataObj.variantRecord;
        utag_data.product_size = newCookiesProductDataObj && newCookiesProductDataObj.productSize;
        utag_data.product_price = newCookiesProductDataObj && newCookiesProductDataObj.productPriceArray;
        utag_data.product_image_url = basketNewObjUrl && basketNewObjUrl.productImageUrl;
        utag_data.product_url = basketNewObjUrl && basketNewObjUrl.productURL;
    }

    if(isUsaEpay === 'true') {
        utag_data.event_category = "USAePay Errors",
        utag_data.event_action = usaEpayErrorCookieObj && usaEpayErrorCookieObj.errorHeading?.replace(/[,!_-]/g, "").replace(/\b(\w)/g, (c) => c.toUpperCase());
        utag_data.event_label = 'card type: '+ cardType
    }

    utag_data.cart_product_image_url_1 = basketNewObjUrl && basketNewObjUrl.productImageUrl[0];
    utag_data.cart_product_image_url_2 = basketNewObjUrl && basketNewObjUrl.productImageUrl[1];
    utag_data.cart_product_image_url_3 = basketNewObjUrl && basketNewObjUrl.productImageUrl[2];

    utag_data.cart_product_name_1 = newCookiesProductDataObj && newCookiesProductDataObj.productNameArray[0];
    utag_data.cart_product_name_2 = newCookiesProductDataObj && newCookiesProductDataObj.productNameArray[1];
    utag_data.cart_product_name_3 = newCookiesProductDataObj && newCookiesProductDataObj.productNameArray[2];

    if(!basketNewObj || basketNewObj.cartDataInfo?.length === 0) {
        [
            'cart_product_id',
            'cart_product_category',
            'cart_product_name',
            'cart_product_brand',
            'cart_product_price',
            'cart_product_quantity',
            'cart_product_variant',
            'cart_product_size',
            'cart_product_image_url',
            'cart_product_url',
            'cart_total_quantity',
            'cart_subtotal',
            'cart_product_name_1',
            'cart_product_name_2',
            'cart_product_name_3',
            'cart_product_image_url_1',
            'cart_product_image_url_2',
            'cart_product_image_url_3',
            'cart_url',
            'core_item_flag',
            'funnel_type'
        ].forEach(e => delete utag_data[e]);

    }
    utag_data.is_cart_active = (
        (basketCookie) => {
            let basketCookieObj = basketCookie && JSON.parse(basketCookie);
            if(basketCookieObj) {
                return basketCookieObj.cartDataInfo?.length > 0 ? "true" : "false";
            }

            return "false";
        })(getCookieValue('mfrm_basket_cookie'));
    `;
};
